/**
 * Start for me — 1-click Memory Prompt Builder (not a chatbot).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bookmark, Mic, PenLine, RefreshCw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { START_FOR_ME_COPY } from '../../content/memoryPromptLibrary';
import { getMemoryPrompt } from '../../utils/getMemoryPrompt';
import {
  getRecentPromptIds,
  pushRecentPromptId,
  savePromptForLater,
} from '../../utils/startForMeStorage';
import { handoffRecordVoice, handoffWriteNow } from '../../utils/startForMeActions';

export function StartForMePromptBuilder({
  person = null,
  personId: personIdProp = null,
  storybookContext = null,
  className = '',
  /** When false, show a compact teaser until expanded. */
  defaultExpanded = true,
  onSaved,
}) {
  const navigate = useNavigate();
  const personId = personIdProp || person?.id || null;
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [prompt, setPrompt] = useState(null);
  const [recentIds, setRecentIds] = useState(() => getRecentPromptIds());

  const pickPrompt = useCallback(
    (excludeId = null) => {
      const next = getMemoryPrompt({
        person,
        recentPromptIds: recentIds,
        storybookContext,
        excludeId,
      });
      setPrompt(next);
      if (next?.id) {
        pushRecentPromptId(next.id);
        setRecentIds(getRecentPromptIds());
      }
    },
    [person, recentIds, storybookContext],
  );

  useEffect(() => {
    if (expanded && !prompt) pickPrompt();
  }, [expanded, prompt, pickPrompt]);

  const handleTryAnother = () => {
    pickPrompt(prompt?.id || null);
  };

  const handleSave = () => {
    if (!prompt) return;
    savePromptForLater({
      promptId: prompt.id,
      mainPrompt: prompt.mainPrompt,
      followUps: prompt.followUps,
      suggestedChapter: prompt.suggestedChapter,
      personId,
    });
    toast.success('Prompt saved for later.');
    onSaved?.();
  };

  if (!expanded) {
    return (
      <div
        className={`rounded-2xl border border-[hsl(38_16%_82%)]/90 bg-gradient-to-br from-[hsl(42_30%_100%)] via-[hsl(42_28%_99%)] to-[hsl(40_24%_96%)] p-5 shadow-sm ${className}`}
        data-testid="start-for-me-teaser"
      >
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b7355]">
          {START_FOR_ME_COPY.label}
        </p>
        <p className="mt-2 font-serif text-lg text-[#1c1917]">{START_FOR_ME_COPY.tagline}</p>
        <p className="mt-1 text-sm leading-relaxed text-[#5c534a]">{START_FOR_ME_COPY.hubIntro}</p>
        <Button
          type="button"
          className="mt-4 rounded-full bg-[#8B7355] hover:bg-[#7A6548]"
          onClick={() => setExpanded(true)}
          data-testid="start-for-me-open"
        >
          <Sparkles className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          {START_FOR_ME_COPY.label}
        </Button>
      </div>
    );
  }

  return (
    <section
      id="start-for-me"
      className={`rounded-2xl border border-[hsl(38_16%_82%)]/90 bg-[hsl(42_28%_99%)] shadow-[0_12px_40px_-24px_hsl(28_14%_18%_/_0.18)] ${className}`}
      data-testid="start-for-me-builder"
    >
      <div className="border-b border-[hsl(38_14%_88%)]/70 px-5 py-4 sm:px-6">
        <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b7355]">
          <Sparkles className="h-3.5 w-3.5" aria-hidden />
          {START_FOR_ME_COPY.label}
        </p>
        <p className="mt-1 text-sm text-[#7a7268]">{START_FOR_ME_COPY.tagline}</p>
      </div>

      {prompt ? (
        <div className="space-y-5 px-5 py-5 sm:px-6 sm:py-6">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8b7355]/90">
              {START_FOR_ME_COPY.todaysPrompt}
            </p>
            <p className="mt-2 font-serif text-xl leading-relaxed text-[#1c1917] sm:text-[1.35rem]">
              {prompt.mainPrompt}
            </p>
          </div>

          {prompt.followUps?.length ? (
            <div className="rounded-xl border border-[hsl(38_14%_86%)]/70 bg-[hsl(42_26%_98%)] px-4 py-3.5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#7a7268]">
                {START_FOR_ME_COPY.followUps}
              </p>
              <ul className="mt-2 space-y-1.5 text-sm leading-relaxed text-[#5c534a]">
                {prompt.followUps.map((q) => (
                  <li key={q} className="flex gap-2">
                    <span className="text-[#c9a961]" aria-hidden>
                      ·
                    </span>
                    <span>{q}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {prompt.suggestedChapter ? (
            <p className="text-sm text-[#5c534a]">
              <span className="font-medium text-[#1c1917]">{START_FOR_ME_COPY.suggestedChapter}:</span>{' '}
              {prompt.suggestedChapter}
            </p>
          ) : null}

          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              type="button"
              className="rounded-full bg-[#8B7355] hover:bg-[#7A6548]"
              onClick={() => handoffWriteNow(navigate, personId, prompt)}
              data-testid="start-for-me-write"
            >
              <PenLine className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              {START_FOR_ME_COPY.writeNow}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="rounded-full border-[hsl(38_16%_82%)]"
              onClick={() => handoffRecordVoice(navigate, personId, prompt)}
              data-testid="start-for-me-record"
            >
              <Mic className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              {START_FOR_ME_COPY.recordVoice}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="rounded-full border-[hsl(38_16%_82%)]"
              onClick={handleSave}
              data-testid="start-for-me-save"
            >
              <Bookmark className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              {START_FOR_ME_COPY.saveForLater}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="rounded-full text-[#5c534a]"
              onClick={handleTryAnother}
              data-testid="start-for-me-try-another"
            >
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              {START_FOR_ME_COPY.tryAnother}
            </Button>
          </div>
        </div>
      ) : (
        <div className="px-5 py-8 text-sm text-muted-foreground sm:px-6">Loading a prompt…</div>
      )}
    </section>
  );
}

export default StartForMePromptBuilder;
